/**
 * **고블린 상인** 창 (`GOBLIN-R0`).
 *
 * 상인이 지금 어디 있는지는 **미러가 혼자 계산한다**(`world/goblin.js` — 결정론
 * 스케줄). 서버에 물을 것이 없고, 구매만 서버가 판정한다(절대규칙 1).
 *
 * ⚠ 값·시간을 문장에 손으로 박지 않는다 — 전부 미러 상수에서 읽는다.
 *   `26,400` 을 글자로 적으면 다음에 값을 바꾸는 날 이 창만 옛 값을 말한다.
 */
import {
  GOBLIN_BSTONE_PRICE, GOBLIN_SHARD_PRICE, GOBLIN_STAY_SECS, goblinAt,
} from '../../world/goblin.js';
import { buyBtn, muted, num, sec, split, npcHead, purse, maxBeforeTax, taxNote, withTax } from './common.js';
import { S, esc } from '../../core/state.js';

/** 남은 시간을 사람 말로 — "몇 초" 는 아무도 안 읽는다 */
const left = (untilSec) => {
  const m = Math.max(0, Math.ceil((untilSec - Date.now() / 1000) / 60));
  return m >= 60 ? `${Math.floor(m / 60)}시간 ${m % 60}분` : `${m}분`;
};

export function render({ y, at }) {
  const now = Date.now() / 1000;
  const v = goblinAt(now);

  const head = npcHead();
  // UNI-Q190 — 개당 값도 세금 포함이다. 두 값 모두 100의 배수라 개당 세금이 정수여서 묶음 합계와 어긋나지 않는다.
  const priceRows = sec('파는 것', `
    <div class="kv"><span>강화석</span><b>${num(withTax(GOBLIN_SHARD_PRICE, y))} 은화</b></div>
    <div class="kv"><span>축복받은 강화석</span><b>${num(withTax(GOBLIN_BSTONE_PRICE, y))} 은화</b></div>`
    + taxNote(y) + muted('강화 재료를 은화로 구매할 수 있다. 구매 수량 제한은 없다.'));

  if (!v) {
    /**
     * 없는 동안에도 창은 **다음 약속**을 말한다 — "없다" 로 끝나면 사람은 다시 안 연다.
     * ⚠ 어디에 올지는 **안 말한다**. 등장 공지가 알리는 것이 이 상인의 놀이다 —
     *   미리 말하면 공지가 뜻을 잃는다. 시각도 창(아침/점심/밤) 단위로만 흘린다.
     */
    return split(head + priceRows, sec('등장 대기',
      muted('하루 세 번(아침 · 점심 · 밤~새벽) 불규칙하게 아무 정착지에나 나타나 '
        + `${GOBLIN_STAY_SECS / 3600}시간 머문다. 나타나면 전체 공지가 뜬다.`)), '');
  }

  const here = sec(`지금 ${v.town.ko}에 있다`,
    `<div class="ux-facts"><div class="ux-fact"><small>떠나기까지</small><b class="ux-number">${left(v.endSec)}</b></div>`
    + `<div class="ux-fact"><small>가방 + 창고 은화</small><b class="ux-number">${num(purse(y))}</b></div></div>`
    + muted('현재 머무는 정착지 안에서만 구매할 수 있다.'));

  const buys = [
    ['shard', '강화석', GOBLIN_SHARD_PRICE],
    ['bstone', '축복받은 강화석', GOBLIN_BSTONE_PRICE],
  ].map(([item, ko, price]) => {
    const raw = S.goblinCounts[item], count = Number(raw), total = count * price;
    const valid = /^\d+$/.test(raw) && Number.isSafeInteger(count) && count > 0 && Number.isSafeInteger(total);
    const busy = S.goblinBusy ? ['처리 중…'] : [];
    return `<div class="sec ux-card"><h3>${ko} <em>개당 ${num(withTax(price, y))} 은화</em></h3>
      <label class="row" for="goblin-${item}-count">구매 수량 (개)
        <input id="goblin-${item}-count" type="text" inputmode="numeric" pattern="[0-9]*" style="width:9em;max-width:100%" value="${esc(raw)}" oninput="act.goblinCount('${item}',this.value)" ${S.goblinBusy ? 'disabled' : ''}>
      </label>
      <div class="kv"><span>총 구매금액 (세금 포함)</span><b>${valid ? `${num(withTax(total, y))} 은화` : '1 이상의 정수를 입력해 달라'}</b></div>
      ${muted(`${num(Math.floor(maxBeforeTax(purse(y), y) / price))}개 구매 가능`)}
      <div class="ux-actions">${buyBtn(S.goblinBusy ? '처리 중…' : '입력 수량 구매', `act.goblinBuy('${item}')`, { y, at, need: 'goblinBuy', price: valid ? withTax(total, y) : 0, more: [...busy, ...(!valid ? ['수량을 다시 입력해 달라'] : [])] })}</div>
      <div class="row">
        ${buyBtn('1개 구매', `act.goblinBuy('${item}',1)`, { y, at, need: 'goblinBuy', price: withTax(price, y), more: busy })}
        ${buyBtn('10개 구매', `act.goblinBuy('${item}',10)`, { y, at, need: 'goblinBuy', price: withTax(price * 10, y), more: busy })}</div></div>`;
  }).join('') + taxNote(y) + muted('수량 제한 없음 · 단가와 결제 금액은 세금 포함');

  return split(head + here, buys, '');
}
